import { AccountCurrencyCode } from '@lune-climate/lune'
import {
    ButtonNew,
    currenciesList,
    LabeledFormSection,
    LuneThemeNew,
    RadioButtonGroup,
    SelectNew,
    Tooltip,
} from '@lune-fe/lune-ui-lib'
import AddIcon from '@mui/icons-material/Add'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Text from '@mui/material/Typography'
import { styled } from '@mui/system'
import { FormApi } from 'final-form'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Field, FieldRenderProps, Form } from 'react-final-form'

import useMappings from 'hooks/useMappings'

import { FormInput } from '../../../../components/FormInput'
import useClientAccounts from '../../../../hooks/useClientAccounts'
import { capitalize } from '../../../../utils'
import { FileDetails } from '../RFQ'
import { MissingMappingData, TypedDapiRfqTemplate } from '../utils/types'
import { isAccountCurrencyCode, validateSheetWithMapping } from '../utils/utils'

export type TemplateReuseInfo = {
    newOrExisting: 'existing'
    selectedExistingMapping: TypedDapiRfqTemplate
    unmappedProperties: MissingMappingData[]
    missingClientAccountInformation: boolean
}
export type DataSheetJobSettings = {
    shipmentsComplete: boolean
    targetClientAccount:
        | { type: 'none' }
        | {
              type: 'create_new'
              name: string
              currency: AccountCurrencyCode
              privacy: PrivacySetting
          }
        | { type: 'per_row' }
        | { type: 'existing'; id: string }
}
export type ClientAccountTarget = DataSheetJobSettings['targetClientAccount']['type']
export type NewCalculationInformation = (TemplateReuseInfo | { newOrExisting: 'new' }) & {
    jobSettings: DataSheetJobSettings
}

enum JobFormKey {
    PRIVACY = 'privacy',
    TARGET_CLIENT_ACCOUNT_TYPE = 'targetClientAccountType',
    CLIENT_ACCOUNT_ID = 'clientAccountId',
    NEW_CLIENT_ACCOUNT_NAME = 'newClientAccountName',
    NEW_CLIENT_ACCOUNT_CURRENCY = 'newClientAccountCurrency',
    MAPPING_ID = 'mappingId',
    SHIPMENTS_COMPLETE = 'shipmentsComplete',
}

interface JobSettingsProps {
    [JobFormKey.PRIVACY]: PrivacySetting
    [JobFormKey.MAPPING_ID]?: string
    [JobFormKey.TARGET_CLIENT_ACCOUNT_TYPE]: ClientAccountTarget
    [JobFormKey.NEW_CLIENT_ACCOUNT_NAME]?: string
    [JobFormKey.NEW_CLIENT_ACCOUNT_CURRENCY]?: AccountCurrencyCode
    [JobFormKey.SHIPMENTS_COMPLETE]: boolean
    [JobFormKey.CLIENT_ACCOUNT_ID]?: string
}

const privacyOptions = ['public', 'hidden'] as const
type PrivacySetting = (typeof privacyOptions)[number]

const { palette, typography } = LuneThemeNew

const required = (value: string | undefined) =>
    value?.trim() ? undefined : 'This is a required field'

function formToJobSettings(formValues: JobSettingsProps): DataSheetJobSettings {
    let targetClientAccount: DataSheetJobSettings['targetClientAccount']
    switch (formValues.targetClientAccountType) {
        case 'create_new':
            targetClientAccount = {
                type: 'create_new',
                name: formValues.newClientAccountName!,
                currency: formValues.newClientAccountCurrency!,
                privacy: formValues.privacy,
            }
            break
        case 'per_row':
            targetClientAccount = { type: 'per_row' }
            break
        case 'existing':
            targetClientAccount = { type: 'existing', id: formValues.clientAccountId! }
            break
        case 'none':
        default:
            targetClientAccount = { type: 'none' }
            break
    }

    return { shipmentsComplete: formValues.shipmentsComplete, targetClientAccount }
}

const NewCalculation: FC<{
    afterSubmit: (val?: NewCalculationInformation) => void
    fileInfo: FileDetails
}> = ({ afterSubmit, fileInfo }) => {
    const [localForm, setLocalForm] = useState<FormApi<JobSettingsProps>>()
    const [unmappedProperties, setUnmappedProperties] = useState<MissingMappingData[]>([])
    const [selectedMappingId, setSelectedMappingId] = useState<string | undefined>()
    const [invalidTemplate, setInvalidTemplate] = useState<boolean>(false)
    const [calculating, setCalculating] = useState<boolean>(false)
    const [clientAccountOption, setClientAccountOption] = useState<ClientAccountTarget>('none')
    const { mappings, loading: isLoadingMappings } = useMappings(fileInfo.sheetType)
    const { paginatedAccounts: clientAccounts } = useClientAccounts()

    const missingClientAccountInfo = useMemo(() => {
        if (clientAccountOption !== 'per_row' || !selectedMappingId) return false
        const selectedMapping = mappings.find((m) => m.id === selectedMappingId)
        return !!selectedMapping && selectedMapping.template.clientAccountName === undefined
    }, [clientAccountOption, selectedMappingId, mappings])

    const StyledHr = useMemo(
        () =>
            styled('hr')(({ theme }) =>
                theme.unstable_sx({
                    border: 0,
                    height: '1px',
                    width: 1,
                    background: palette.Grey300,
                    margin: 0,
                }),
            ),
        [palette.Grey300],
    )

    const formItemStyling = {
        height: '48px',
        width: '100%',
        border: `1px solid ${palette.Grey300} !important`,
        borderRadius: '4px',
    }

    const FormSelectPrivacy = ({
        input: { onChange, value },
    }: FieldRenderProps<PrivacySetting>) => (
        <SelectNew
            sx={{
                ...formItemStyling,
                zIndex: 0,
            }}
            value={value}
            items={privacyOptions.map((opt) => ({
                label: capitalize(opt),
                value: opt,
            }))}
            onChange={onChange}
        />
    )

    function FormSelectClientAccount(selectedAccountId?: string): JSX.Element {
        // Find the selected client account to show privacy information
        const selectedAccount =
            selectedAccountId && clientAccounts?.data
                ? clientAccounts.data.find((a) => a.id === selectedAccountId)
                : null
        const privacyInfo = selectedAccount?.externalPagesVisibility
        return privacyInfo ? (
            <Tooltip
                title={
                    <Text variant="caption" sx={{ color: palette.White }}>
                        Client account sustainability page visibility is set to {privacyInfo} and
                        can only be edited by account holders via the Lune API
                    </Text>
                }
                placement="right"
                style={{ maxWidth: 'fit-content' }}
            >
                <Text
                    variant="caption"
                    sx={{
                        color: palette.Grey600,
                        cursor: 'help',
                        display: 'inline-block',
                        width: 'fit-content',
                    }}
                >
                    Sustainability page visibility: {capitalize(privacyInfo)}
                </Text>
            </Tooltip>
        ) : (
            <></>
        )
    }

    const clientAccountDropdownItems = useMemo(
        () => [
            {
                label: 'Multiple client accounts',
                value: 'per_row',
                icon: <TableChartOutlinedIcon />,
                dividerAfter: true,
            },
            {
                label: 'Create new client account',
                value: 'create_new',
                icon: <AddIcon />,
                dividerAfter: true,
            },
            ...(clientAccounts?.data.map((account) => ({
                label: account.name,
                value: account.id,
            })) ?? []),
        ],
        [clientAccounts],
    )

    // Revalidate mapping when client account option changes
    useEffect(() => {
        if (localForm) {
            const mappingId = localForm.getState().values[JobFormKey.MAPPING_ID]
            validateMapping(mappingId)
        }
    }, [clientAccountOption])

    const handleClientAccountDropdownChange = useCallback(
        (rawValue: string | null, form: FormApi<JobSettingsProps>) => {
            const value = rawValue ?? 'none'
            if (value === 'none') {
                setClientAccountOption('none')
                form.change(JobFormKey.TARGET_CLIENT_ACCOUNT_TYPE, 'none')
                form.change(JobFormKey.CLIENT_ACCOUNT_ID, undefined)
            } else if (value === 'create_new') {
                setClientAccountOption('create_new')
                form.change(JobFormKey.TARGET_CLIENT_ACCOUNT_TYPE, 'create_new')
                form.change(JobFormKey.CLIENT_ACCOUNT_ID, undefined)
            } else if (value === 'per_row') {
                setClientAccountOption('per_row')
                form.change(JobFormKey.TARGET_CLIENT_ACCOUNT_TYPE, 'per_row')
                form.change(JobFormKey.CLIENT_ACCOUNT_ID, undefined)
            } else {
                // Existing account selected by ID
                setClientAccountOption('existing')
                form.change(JobFormKey.TARGET_CLIENT_ACCOUNT_TYPE, 'existing')
                form.change(JobFormKey.CLIENT_ACCOUNT_ID, value)
            }
        },
        [],
    )

    const FormSelectMapping = useCallback(
        ({ input: { onChange, value } }: FieldRenderProps<string>) => {
            const disabledSelect = (label: string) => (
                <SelectNew
                    placeholder={label}
                    disabled={true}
                    sx={formItemStyling}
                    value={null}
                    items={[]}
                    onChange={() => {}}
                />
            )
            const loadingMappings = disabledSelect('Loading mappings')
            const noMappings = disabledSelect('No mappings')
            const validMappings = (
                <SelectNew
                    placeholder={'Mapping'}
                    sx={formItemStyling}
                    error={
                        invalidTemplate
                            ? 'This mapping is not compatible with the file provided'
                            : undefined
                    }
                    value={value}
                    items={mappings.map((i) => ({
                        label: i.name,
                        value: i.id,
                    }))}
                    onChange={(e) => {
                        setSelectedMappingId(e)
                        validateMapping(e)
                        onChange(e)
                    }}
                />
            )
            return isLoadingMappings
                ? loadingMappings
                : mappings.length
                  ? validMappings
                  : noMappings
        },
        [mappings, clientAccountOption, invalidTemplate],
    )

    const FormRadioButtons = ({ input: { onChange, value } }: FieldRenderProps<boolean>) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <RadioButtonGroup
                name={'Shipment status'}
                defaultValue={true}
                onChange={(val) => onChange(val.target.value === 'true')}
                value={value}
                items={[
                    {
                        label: 'Shipment',
                        value: true,
                    },
                    {
                        label: 'Pre-shipment',
                        value: false,
                    },
                ]}
            />
        </Box>
    )

    const FormCurrencyPicker = ({ input: { onChange, value }, meta }: FieldRenderProps<string>) => (
        <SelectNew
            placeholder={'Select a currency'}
            sx={formItemStyling}
            error={meta.touched ? meta.error : undefined}
            items={currenciesList}
            value={value}
            onChange={(e) => {
                if (isAccountCurrencyCode(e)) {
                    onChange(e)
                } else {
                    throw new Error(
                        `Programming Error. Unexpected account currency option selected: ${e}`,
                    )
                }
            }}
        />
    )

    function mappingIdToMapping(mappingId: string): TypedDapiRfqTemplate {
        const selectedMapping = mappings.find((mapping) => mapping.id === mappingId)
        if (!selectedMapping) {
            throw new Error('Programming error: Invalid mapping selection')
        }
        return selectedMapping
    }

    const onSubmit = async (formValues: JobSettingsProps) => {
        const newJobSettings = formToJobSettings(formValues)
        if (formValues.mappingId) {
            afterSubmit({
                newOrExisting: 'existing',
                selectedExistingMapping: mappingIdToMapping(formValues.mappingId),
                jobSettings: newJobSettings,
                unmappedProperties,
                missingClientAccountInformation: missingClientAccountInfo,
            })
        } else {
            afterSubmit({ newOrExisting: 'new', jobSettings: newJobSettings })
        }
    }

    async function validateMapping(mappingId?: string) {
        if (!mappingId) {
            return
        }
        const selectedMapping = mappings.find((mapping) => mapping.id === mappingId)
        if (!selectedMapping) {
            throw new Error('Programming error: Invalid mapping selection')
        }
        const validation = await validateSheetWithMapping(fileInfo, selectedMapping.template)
        if (validation === 'new_template_required') {
            setInvalidTemplate(true)
            setUnmappedProperties([])
        } else {
            setInvalidTemplate(false)
            setUnmappedProperties(validation.missingMappingProperties)
        }
    }

    return (
        <Stack
            direction="column"
            sx={{
                width: '100%',
            }}
            spacing={{
                xs: 5,
            }}
        >
            <Text variant={'h5'} textAlign={'center'}>
                New logistics spreadsheet calculation
            </Text>
            <Box
                sx={{
                    width: '100%',
                    minHeight: 72,
                    borderRadius: '16px',
                    padding: '24px',
                    gap: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: palette.Grey50,
                    boxSizing: 'border-box',
                    marginBottom: '40px',
                }}
            >
                <TableChartOutlinedIcon
                    sx={{
                        color: palette.Black,
                        fontSize: 24,
                    }}
                />
                <Text
                    variant="body2"
                    sx={{
                        color: palette.Grey600,
                        fontWeight: typography.fontWeightRegular,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                    }}
                >
                    {fileInfo.filename}
                </Text>
            </Box>
            <Form
                onSubmit={onSubmit}
                initialValues={{
                    [JobFormKey.PRIVACY]: 'public',
                    [JobFormKey.CLIENT_ACCOUNT_ID]: undefined,
                    [JobFormKey.TARGET_CLIENT_ACCOUNT_TYPE]: 'none',
                    [JobFormKey.NEW_CLIENT_ACCOUNT_NAME]: undefined,
                    [JobFormKey.NEW_CLIENT_ACCOUNT_CURRENCY]: undefined,
                    [JobFormKey.MAPPING_ID]: undefined,
                    [JobFormKey.SHIPMENTS_COMPLETE]: false,
                }}
                render={({ form }) => {
                    useEffect(() => {
                        if (!localForm) {
                            setLocalForm(form)
                        }
                    }, [form, localForm])
                    return (
                        <Stack direction="column" sx={{ width: '100%' }} spacing={{ xs: 4 }}>
                            <Field name={JobFormKey.MAPPING_ID} component={FormSelectMapping} />
                            {fileInfo.sheetType === 'logistics_user_template' && (
                                <>
                                    <SelectNew
                                        placeholder={'Select a client account'}
                                        sx={formItemStyling}
                                        value={
                                            clientAccountOption === 'none'
                                                ? null
                                                : clientAccountOption === 'existing'
                                                  ? (form.getState().values[
                                                        JobFormKey.CLIENT_ACCOUNT_ID
                                                    ] ?? null)
                                                  : clientAccountOption
                                        }
                                        items={clientAccountDropdownItems}
                                        onChange={(value) =>
                                            handleClientAccountDropdownChange(value, form)
                                        }
                                    />
                                    {clientAccountOption === 'existing' &&
                                        (() => {
                                            const selectedAccountId =
                                                form.getState().values[JobFormKey.CLIENT_ACCOUNT_ID]
                                            return FormSelectClientAccount(selectedAccountId)
                                        })()}
                                    {clientAccountOption === 'create_new' && (
                                        <>
                                            <Stack direction="column" spacing={{ xs: 4 }}>
                                                <LabeledFormSection
                                                    nested={true}
                                                    title={'Client account name'}
                                                >
                                                    <Field
                                                        name={JobFormKey.NEW_CLIENT_ACCOUNT_NAME}
                                                        validate={required}
                                                        component={FormInput}
                                                        label="Account Name"
                                                        sx={formItemStyling}
                                                    />
                                                </LabeledFormSection>
                                                <LabeledFormSection
                                                    nested={true}
                                                    title={'Currency'}
                                                >
                                                    <Field
                                                        name={
                                                            JobFormKey.NEW_CLIENT_ACCOUNT_CURRENCY
                                                        }
                                                        validate={required}
                                                        component={FormCurrencyPicker}
                                                        label="Currency"
                                                    />
                                                </LabeledFormSection>
                                                <LabeledFormSection
                                                    nested={true}
                                                    title={'Sustainability page visibility'}
                                                >
                                                    <Field
                                                        name={JobFormKey.PRIVACY}
                                                        component={FormSelectPrivacy}
                                                    />
                                                </LabeledFormSection>
                                            </Stack>
                                            <StyledHr />
                                        </>
                                    )}
                                    <LabeledFormSection
                                        title={'Shipment status'}
                                        sx={{
                                            zIndex: 0,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start',
                                            }}
                                        >
                                            <Field
                                                name={JobFormKey.SHIPMENTS_COMPLETE}
                                                component={FormRadioButtons}
                                            />
                                            <Tooltip
                                                // 53 = 32 margin top + 21 title line height
                                                style={{ marginTop: '-53px' }}
                                                title={
                                                    <Text
                                                        variant="caption"
                                                        sx={{ color: palette.White }}
                                                    >
                                                        This determines whether the calculation
                                                        represents a real shipment.
                                                        <br />
                                                        <br />
                                                        Completed shipments are included in total
                                                        emissions and reporting.
                                                        <br />
                                                        <br />
                                                        Not shipped is treated as a test or
                                                        exploratory calculation. Not counted in your
                                                        emissions totals.
                                                    </Text>
                                                }
                                            >
                                                <InfoOutlinedIcon
                                                    sx={{
                                                        color: palette.Black,
                                                        fontSize: 20,
                                                        zIndex: 0,
                                                    }}
                                                />
                                            </Tooltip>
                                        </Box>
                                    </LabeledFormSection>
                                </>
                            )}
                        </Stack>
                    )
                }}
            />
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                }}
            >
                <ButtonNew
                    style={'outline'}
                    onClick={() => {
                        if (!localForm) return
                        // Remove selected mapping if we want to start a new one
                        localForm.change(JobFormKey.MAPPING_ID, undefined)
                        localForm.submit()
                    }}
                >
                    Start new mapping
                </ButtonNew>
                <ButtonNew
                    loading={calculating}
                    disabled={
                        !localForm ||
                        !localForm.getState().values[JobFormKey.MAPPING_ID] ||
                        invalidTemplate
                    }
                    onClick={() => {
                        if (calculating) return
                        // Submitting an invalid form only marks its fields as touched, showing their
                        // errors, so there's nothing to wait for
                        if (!localForm?.getState().hasValidationErrors) {
                            setCalculating(true)
                        }
                        localForm?.submit()
                    }}
                >
                    {missingClientAccountInfo ? 'Add account information' : 'Calculate emissions'}
                </ButtonNew>
            </Box>
        </Stack>
    )
}

export default NewCalculation
